/**
 * NMS Thumbnail Navigation Enhancement
 * Adds smooth vertical navigation for desktop thumbnail gallery
 *
 * Features:
 * - Desktop-only vertical thumbnail navigation
 * - Responsive behavior (1000px+ for desktop)
 * - Smooth scroll animations with optimal viewport positioning
 * - Proper state management and event handling
 * - Automatic initialization and cleanup
 */

// Define base NMS Component class if not already defined
window.NMSComponent =
  window.NMSComponent ||
  class {
    constructor(element) {
      this.element = element;
    }

    init() {}

    dispatchCustomEvent(eventName, detail = {}) {
      const event = new CustomEvent(`nms:${eventName}`, {
        detail: {
          component: this,
          element: this.element,
          ...detail,
          timestamp: Date.now(),
        },
        bubbles: true,
        cancelable: true,
      });

      this.element.dispatchEvent(event);
    }
  };

class NMSThumbnailNavigation extends window.NMSComponent {
  static componentName = 'nms-thumbnail-navigation';

  // Configuration constants
  static CONFIG = {
    DESKTOP_BREAKPOINT: 1000,
    VISIBLE_THUMBNAIL_COUNT: 4,
    ANIMATION_DURATION: 250,
    SELECTION_DELAY: 50,
    BUTTON_UPDATE_DELAY: 100,
  };

  constructor(element) {
    super(element);

    // Core elements
    this.thumbnailScroller = null;
    this.prevButton = null;
    this.nextButton = null;
    this.mutationObserver = null;

    // State management
    this.isCurrentlyDesktop = this.isDesktop();
    this.isInitialized = false;

    // Bind methods
    this.handlePrevClick = this.handlePrevClick.bind(this);
    this.handleNextClick = this.handleNextClick.bind(this);
    this.updateButtonStates = this.updateButtonStates.bind(this);
    this.handleThumbnailClick = this.handleThumbnailClick.bind(this);
    this.handleMutation = this.handleMutation.bind(this);
    this.handleResize = this.debounce(this.handleResize.bind(this), 250);
    this._boundVisibilityHandler = this.handleVisibilityChange.bind(this);

    this.init();
  }

  /**
   * Initialize the component
   */
  init() {
    if (!this.setupElements()) {
      return;
    }

    this.bindEvents();
    this.initializeDesktopFeatures();
    this.isInitialized = true;

    // Listen for resize events
    window.addEventListener('resize', this.handleResize);

    // Listen for visibility changes to pause/resume observer
    document.addEventListener('visibilitychange', this._boundVisibilityHandler);
  }

  /**
   * Handle visibility change - pause observer when hidden
   */
  handleVisibilityChange() {
    if (document.hidden) {
      // Disconnect observer when tab is hidden
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }
    } else {
      // Reconnect observer when tab becomes visible
      if (this.mutationObserver && this.thumbnailScroller) {
        this.mutationObserver.observe(this.thumbnailScroller, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['aria-current'],
        });
        // Sync state after returning to tab
        this.updateButtonStates();
      }
    }
  }

  /**
   * Setup DOM element references
   * @returns {boolean} Success status
   */
  setupElements() {
    this.thumbnailScroller = this.element.querySelector('.product-gallery__thumbnail-scroller');
    this.prevButton = this.element.querySelector('carousel-prev-button button');
    this.nextButton = this.element.querySelector('carousel-next-button button');

    if (!this.thumbnailScroller || !this.prevButton || !this.nextButton) {
      console.warn('NMSThumbnailNavigation: Required elements not found');
      return false;
    }

    return true;
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    if (this.isDesktop()) {
      this.bindDesktopEvents();
    }

    this.bindMutationObserver();
  }

  /**
   * Bind desktop-specific events
   */
  bindDesktopEvents() {
    this.prevButton.addEventListener('click', this.handlePrevClick);
    this.nextButton.addEventListener('click', this.handleNextClick);
    this.thumbnailScroller.addEventListener('scroll', this.updateButtonStates);
    this.thumbnailScroller.addEventListener('click', this.handleThumbnailClick);
  }

  /**
   * Remove desktop-specific events
   */
  unbindDesktopEvents() {
    this.prevButton.removeEventListener('click', this.handlePrevClick);
    this.nextButton.removeEventListener('click', this.handleNextClick);
    this.thumbnailScroller.removeEventListener('scroll', this.updateButtonStates);
    this.thumbnailScroller.removeEventListener('click', this.handleThumbnailClick);
  }

  /**
   * Setup mutation observer for aria-current changes
   */
  bindMutationObserver() {
    this.mutationObserver = new MutationObserver(this.handleMutation);
    this.mutationObserver.observe(this.thumbnailScroller, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-current'],
    });
  }

  /**
   * Initialize desktop-specific features
   */
  initializeDesktopFeatures() {
    if (!this.isDesktop()) return;

    this.updateButtonStates();

    // Ensure selected thumbnail is visible on initialization
    setTimeout(() => {
      this.scrollSelectedThumbnailIntoView();
    }, NMSThumbnailNavigation.CONFIG.BUTTON_UPDATE_DELAY);
  }

  /**
   * Handle previous button click
   */
  handlePrevClick(event) {
    if (!this.isDesktop()) return;

    event.preventDefault();
    event.stopPropagation();
    this.navigateToThumbnail('prev');
  }

  /**
   * Handle next button click
   */
  handleNextClick(event) {
    if (!this.isDesktop()) return;

    event.preventDefault();
    event.stopPropagation();
    this.navigateToThumbnail('next');
  }

  /**
   * Handle thumbnail click events
   */
  handleThumbnailClick(event) {
    if (!this.isDesktop()) return;

    if (event.target.closest('.product-gallery__thumbnail')) {
      setTimeout(() => {
        this.updateButtonStates();
      }, NMSThumbnailNavigation.CONFIG.BUTTON_UPDATE_DELAY);
    }
  }

  /**
   * Handle mutation events (aria-current changes)
   */
  handleMutation(mutations) {
    if (document.hidden) return;
    if (!this.isDesktop()) return;

    const hasAriaCurrentChange = mutations.some(
      mutation => mutation.type === 'attributes' && mutation.attributeName === 'aria-current'
    );

    if (hasAriaCurrentChange) {
      this.scrollSelectedThumbnailIntoView();
      this.updateButtonStates();
    }
  }

  /**
   * Handle window resize events
   */
  handleResize() {
    const isDesktop = this.isDesktop();

    // Only re-initialize if device type changed
    if (isDesktop !== this.isCurrentlyDesktop) {
      this.reinitializeForDeviceType(isDesktop);
      this.isCurrentlyDesktop = isDesktop;
    }
  }

  /**
   * Reinitialize for different device type
   */
  reinitializeForDeviceType(isDesktop) {
    // Clean up existing events
    this.unbindDesktopEvents();

    if (isDesktop) {
      // Re-setup elements in case DOM changed
      if (this.setupElements()) {
        this.bindDesktopEvents();
        this.initializeDesktopFeatures();
      }
    } else {
      // Reset button states when switching to mobile
      this.resetButtonStates();
    }
  }

  /**
   * Navigate to previous or next thumbnail
   * @param {string} direction - 'prev' or 'next'
   */
  navigateToThumbnail(direction) {
    const thumbnails = this.getThumbnails();
    if (thumbnails.length === 0) return;

    const currentIndex = this.getCurrentThumbnailIndex(thumbnails);
    let nextIndex;

    if (direction === 'prev') {
      nextIndex = Math.max(0, currentIndex - 1);
    } else {
      nextIndex = Math.min(thumbnails.length - 1, currentIndex + 1);
    }

    // Only proceed if we're actually changing thumbnails
    if (nextIndex === currentIndex) return;

    // Select the target thumbnail
    this.selectThumbnail(thumbnails[nextIndex], nextIndex);
  }

  /**
   * Select a specific thumbnail
   * @param {Element} thumbnail - The thumbnail element to select
   * @param {number} index - The thumbnail index
   */
  selectThumbnail(thumbnail, index) {
    thumbnail.click();

    // Ensure thumbnail is optimally positioned in viewport
    setTimeout(() => {
      this.ensureOptimalViewport(index);
    }, NMSThumbnailNavigation.CONFIG.SELECTION_DELAY);
  }

  /**
   * Ensure thumbnail is optimally positioned in 4-thumbnail viewport
   * @param {number} thumbnailIndex - Index of target thumbnail
   */
  ensureOptimalViewport(thumbnailIndex) {
    const thumbnails = this.getThumbnails();
    if (thumbnails.length === 0) return;

    const { thumbnailHeight, gap } = this.getThumbnailDimensions();
    const itemHeight = thumbnailHeight + gap;

    const optimalStartIndex = this.calculateOptimalStartIndex(thumbnailIndex, thumbnails.length);
    const targetScrollTop = optimalStartIndex * itemHeight;
    const currentScrollTop = this.thumbnailScroller.scrollTop;

    if (targetScrollTop !== currentScrollTop) {
      this.animateScrollTo(targetScrollTop, currentScrollTop);
    }
  }

  /**
   * Calculate optimal start index for 4-thumbnail viewport
   * @param {number} targetIndex - Index of thumbnail to ensure is visible
   * @param {number} totalCount - Total number of thumbnails
   * @returns {number} Optimal start index
   */
  calculateOptimalStartIndex(targetIndex, totalCount) {
    const { VISIBLE_THUMBNAIL_COUNT } = NMSThumbnailNavigation.CONFIG;
    const maxStartIndex = Math.max(0, totalCount - VISIBLE_THUMBNAIL_COUNT);

    if (targetIndex <= 1) {
      return 0;
    } else if (targetIndex >= totalCount - 2) {
      return maxStartIndex;
    } else {
      return Math.max(0, Math.min(maxStartIndex, targetIndex - 1));
    }
  }

  /**
   * Animate scroll to target position
   * @param {number} targetScrollTop - Target scroll position
   * @param {number} startScrollTop - Starting scroll position
   */
  animateScrollTo(targetScrollTop, startScrollTop) {
    const { ANIMATION_DURATION } = NMSThumbnailNavigation.CONFIG;
    const startTime = performance.now();

    // Cubic ease-out function for smooth animation
    const easeOut = t => 1 - Math.pow(1 - t, 3);

    const animate = currentTime => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
      const easedProgress = easeOut(progress);

      const currentScrollTop = startScrollTop + (targetScrollTop - startScrollTop) * easedProgress;
      this.thumbnailScroller.scrollTop = currentScrollTop;

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }

  /**
   * Scroll selected thumbnail into view
   */
  scrollSelectedThumbnailIntoView() {
    if (!this.isDesktop()) return;

    const selectedThumbnail = this.thumbnailScroller.querySelector('.product-gallery__thumbnail[aria-current="true"]');
    if (!selectedThumbnail) return;

    const thumbnails = this.getThumbnails();
    const selectedIndex = Array.from(thumbnails).indexOf(selectedThumbnail);

    if (selectedIndex !== -1) {
      this.ensureOptimalViewport(selectedIndex);
    }
  }

  /**
   * Update navigation button states
   */
  updateButtonStates() {
    if (!this.isDesktop() || !this.prevButton || !this.nextButton) return;

    const thumbnails = this.getThumbnails();
    if (thumbnails.length === 0) return;

    const currentIndex = this.getCurrentThumbnailIndex(thumbnails);

    // Update previous button state
    this.updateButtonState(this.prevButton, currentIndex > 0);

    // Update next button state
    this.updateButtonState(this.nextButton, currentIndex < thumbnails.length - 1);
  }

  /**
   * Update individual button state
   * @param {Element} button - Button element
   * @param {boolean} enabled - Whether button should be enabled
   */
  updateButtonState(button, enabled) {
    button.style.opacity = enabled ? '1' : '0.3';
    button.style.pointerEvents = enabled ? 'auto' : 'none';
  }

  /**
   * Reset button states to default (for mobile)
   */
  resetButtonStates() {
    if (!this.prevButton || !this.nextButton) return;

    // Reset to default state
    this.prevButton.style.opacity = '';
    this.prevButton.style.pointerEvents = '';
    this.nextButton.style.opacity = '';
    this.nextButton.style.pointerEvents = '';
  }

  /**
   * Get all thumbnail elements
   * @returns {NodeList} Thumbnail elements
   */
  getThumbnails() {
    return this.thumbnailScroller.querySelectorAll('.product-gallery__thumbnail');
  }

  /**
   * Get current thumbnail index
   * @param {NodeList} thumbnails - Thumbnail elements
   * @returns {number} Current thumbnail index
   */
  getCurrentThumbnailIndex(thumbnails) {
    const currentActive = this.thumbnailScroller.querySelector('.product-gallery__thumbnail[aria-current="true"]');
    return currentActive ? Array.from(thumbnails).indexOf(currentActive) : 0;
  }

  /**
   * Get thumbnail dimensions and spacing
   * @returns {Object} Dimensions object
   */
  getThumbnailDimensions() {
    const thumbnails = this.getThumbnails();
    if (thumbnails.length === 0) return { thumbnailHeight: 0, gap: 0 };

    const thumbnailHeight = thumbnails[0].offsetHeight;
    const containerStyles = getComputedStyle(this.thumbnailScroller);
    const gap = parseFloat(containerStyles.gap) || 0;

    return { thumbnailHeight, gap };
  }

  /**
   * Check if current viewport is desktop
   * @returns {boolean} Is desktop
   */
  isDesktop() {
    return window.innerWidth >= NMSThumbnailNavigation.CONFIG.DESKTOP_BREAKPOINT;
  }

  /**
   * Simple debounce utility
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} Debounced function
   */
  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  /**
   * Cleanup and destroy component
   */
  destroy() {
    // Remove event listeners
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('visibilitychange', this._boundVisibilityHandler);
    this.unbindDesktopEvents();

    // Disconnect mutation observer
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    // Clear references
    this.thumbnailScroller = null;
    this.prevButton = null;
    this.nextButton = null;

    super.destroy();
  }
}

// Component Registration and Initialization
// =======================================

// Fallback for browsers without NMS framework
if (typeof window.NMSComponent === 'undefined') {
  window.NMSComponent = class {
    constructor(element) {
      this.element = element;
    }
    destroy() {}
  };
}

/**
 * Initialize thumbnail navigation components
 */
function initializeNMSThumbnailNavigation(container = document) {
  if (!window.NMSComponent) {
    console.warn('NMSThumbnailNavigation: NMS framework not available');
    return;
  }

  const selector = '.product-gallery__carousel-with-arrows';
  const elements = container.querySelectorAll(selector);

  elements.forEach(element => {
    if (!element.hasAttribute('data-nms-thumbnail-nav-initialized')) {
      new NMSThumbnailNavigation(element);
      element.setAttribute('data-nms-thumbnail-nav-initialized', 'true');
    }
  });
}

// Auto-initialization
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeNMSThumbnailNavigation);
} else {
  initializeNMSThumbnailNavigation();
}

// Listen for color content updates and re-initialize within the updated container
document.addEventListener('nms:color-content-updated', event => {
  const { container, source } = event.detail;
  initializeNMSThumbnailNavigation(container);
});

// Handle Shopify theme editor reloads
document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('.product-gallery__carousel-with-arrows')) {
    initializeNMSThumbnailNavigation();
  }
});

// Export for potential external use
window.NMSThumbnailNavigation = NMSThumbnailNavigation;
